import { api } from "aldoo-ra/API"

/**
 * StorePIDValidator
 * Validates Store Product IDs according to Apple's specifications:
 * - Must start with a letter or number (not a period, underscore, or hyphen)
 * - Can only include alphanumeric characters, underscores (_), periods (.), and hyphens (-)
 * - Must be unique within the database
 * - Max 255 characters (enforced by MaxLength validator)
 *
 * Usage: validators: { StorePIDValidator: {} }
 *
 * @param {object} errors - The errors object to populate
 * @param {any} value - The field value to validate
 * @param {object} item - The full item being edited
 * @param {object} params - Validator parameters (none required)
 * @param {object} field - The field definition
 */
export default async function storePIDValidator(errors, value, item, params, field) {
  if (!value) {
    errors[field.id] = "Product ID is required"
    return
  }

  const stringValue = String(value).trim()

  // Check minimum length
  if (stringValue.length < 3) {
    errors[field.id] = "Product ID must be at least 3 characters long"
    return
  }

  // Check for uppercase characters
  const hasUppercase = /[A-Z]/.test(stringValue)
  if (hasUppercase) {
    errors[field.id] = "Product ID must be lowercase. For example: \"premium-weekly-plan\""
    return
  }

  // Check if starts with letter or number
  const startsWithLetterOrNumber = /^[a-z0-9]/.test(stringValue)
  if (!startsWithLetterOrNumber) {
    errors[field.id] = "Product ID must start with a lowercase letter or number"
    return
  }

  // Check for valid characters (lowercase alphanumeric, underscore, period, hyphen)
  const validCharacters = /^[a-z0-9_.-]+$/.test(stringValue)
  if (!validCharacters) {
    errors[field.id] = "Product ID can only contain lowercase letters, numbers, underscores (_), periods (.), and hyphens (-). For example: \"premium-weekly-plan\""
    return
  }

  // Check for duplicate PID in the database
  try {
    const crudAPI = api("crud")

    const match = {
      pid: stringValue,
      deleted: { $ne: true } // Exclude soft-deleted products
    }

    // Only exclude current item if it has an _id
    if (item._id) {
      match._id = { $ne: item._id }
    }

    const response = await crudAPI({
      get: {
        model: "StoreProduct",
        match,
        limit: 10 // Get multiple to see all duplicates
      }
    })

    if (response && response.length > 0) {
      errors[field.id] = "Product ID already exists. Please choose a unique ID."
    }
  } catch (error) {
    console.error("Error checking PID uniqueness:", error)
    // Don't block saving if uniqueness check fails, let the server handle it
  }
}
